import { playdataNewsListPageQueryOptions } from "@data/public-page-data-query"
import * as m from "@paraglide/messages"
import { PlaydataSection } from "@playdata/PlaydataPrimitives"
import { playdataPublishDate } from "@playdata/playdata-date"
import { playdataNewsCoverSrc } from "@playdata/playdata-news-covers"
import type { SupportedLocale } from "@site-types/content"
import type { NewsListPageData } from "@site-types/public-page-data"
import { createFileRoute, Link } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

export const Route = createFileRoute("/$locale/playdata/news/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(playdataNewsListPageQueryOptions(params.locale as SupportedLocale)),
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_news_meta_description({}, { locale }),
      locale,
      path: "/playdata/news",
      title: m.playdata_news_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

/** `NewsItem.slug` is `{ current }` from the SQL reads but the shared type also
 * permits a bare string, so both shapes are unwrapped here. */
function newsSlug(slug: NewsListPageData["news"][number]["slug"]): string {
  if (typeof slug === "string") return slug
  return slug?.current ?? ""
}

/**
 * The live list: four columns of cards, each a bordered thumbnail, a small
 * date, the title in bold and an underlined "Read More".
 */
function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale
  const { news: posts } = Route.useLoaderData() as NewsListPageData

  return (
    <PlaydataSection>
      <h1 className="mb-[4rem] text-center text-[3rem] md:text-[3.6rem]">
        <strong>{m.playdata_news_heading({}, { locale })}</strong>
      </h1>

      {posts.length === 0 ? (
        <p className="text-center text-[1.6rem]">{m.playdata_news_empty({}, { locale })}</p>
      ) : (
        <ul className="grid list-none gap-x-[3.2rem] gap-y-[4.8rem] p-0 sm:grid-cols-2 lg:grid-cols-4">
          {posts.map((post) => {
            const slug = newsSlug(post.slug)
            const cover = playdataNewsCoverSrc(slug, post.newsCoverImage)
            return (
              <li key={`${post.language}-${slug}`}>
                <Link
                  className="group flex h-full flex-col gap-[1rem]"
                  params={{ locale, slug }}
                  to="/$locale/playdata/news/$slug"
                >
                  {cover ? (
                    <img
                      alt=""
                      className="h-auto w-full border border-[#dce1e1] shadow-[2px_2px_5px_rgba(0,0,0,0.07)]"
                      height={450}
                      loading="lazy"
                      src={cover}
                      width={800}
                    />
                  ) : null}
                  {post.publishDate ? (
                    <time
                      className="text-[1.3rem]"
                      dateTime={post.publishDate}
                    >
                      {playdataPublishDate(post.publishDate)}
                    </time>
                  ) : null}
                  <h2 className="text-[2rem] leading-[1.4] group-hover:underline">
                    <strong>{post.title}</strong>
                  </h2>
                  <span className="mt-auto text-[1.3rem] text-play-pink underline underline-offset-4">
                    {m.playdata_news_list_readMore({}, { locale })}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </PlaydataSection>
  )
}
